/**
 * 研弈引力 - 前端共享常量
 * 
 * 9 种内容体裁列表、3 大功能模块定义、按次计费等。
 */

import type { StyleType, ModuleType, SubscriptionType } from './types'

// ===== 9 种内容体裁 =====
//
// 这一维度回答的是「这篇文章写成什么形状」（探店 / 测评 / 攻略…），
// 而不是「用什么口气写」。口气由后台「品牌调性」(brand_config.tone_style) 统一控制。
// value 必须与后端 prompts.py 的 _STYLE_TEMPLATES key 完全一致，不得各写各的。
export interface StyleOption {
  value: StyleType
  label: string
  icon: string
  description: string
}

/** 默认预选体裁：最百搭，拿不准就选它（与后端 DEFAULT_STYLE 同值） */
export const DEFAULT_STYLE: StyleType = 'guide'

export const STYLE_LIST: StyleOption[] = [
  {
    value: 'guide',
    label: '点餐攻略',
    icon: '🧭',
    description: '教顾客怎么点不踩雷，实用，容易被收藏',
  },
  {
    value: 'rank',
    label: '必点榜单',
    icon: '🏆',
    description: 'TOP3、TOP5 排行，一眼看懂该点哪几道',
  },
  {
    value: 'review',
    label: '真实测评',
    icon: '⭐',
    description: '按口味、分量、性价比逐项打分点评，显得实在可信',
  },
  {
    value: 'topic',
    label: '话题互动',
    icon: '💬',
    description: '抛个问题让人来评论区聊，适合催互动',
  },
  {
    value: 'newproduct',
    label: '新品尝鲜',
    icon: '🆕',
    description: '上新专用，讲清新品和老款有什么不一样',
  },
  {
    value: 'festival',
    label: '节日应景',
    icon: '🎉',
    description: '结合节日节气造气氛，适合过节前后引客',
  },
  {
    value: 'tier',
    label: '梯度榜单',
    icon: '📶',
    description: '把同类的「店」按值得去程度分梯队（夯/顶/人上人），像本地人的一把尺',
  },
  {
    value: 'checklist',
    label: '打卡清单',
    icon: '✅',
    description: '把本地值得去的「店」整理成可勾选清单，实用、容易被收藏',
  },
  {
    value: 'chat',
    label: '私聊推荐',
    icon: '📩',
    description: '图片直接做成微信聊天截图，像本地朋友一条条发消息安利你',
  },
]

// ===== 体裁可见性（按品牌控制，2026-10-08）=====
//
// 前 6 种体裁（guide/rank/review/topic/newproduct/festival）暂存体验问题，
// 需要逐个单独调优；调好之前，生产品牌只暴露后 3 种（tier/checklist/chat）。
// 测试品牌（蚝红天虾）保留全部 9 种 + 话题输入框，供后续逐项验证。
export const HIDDEN_LEGACY_STYLES: ReadonlySet<StyleType> = new Set<StyleType>([
  'guide',
  'rank',
  'review',
  'topic',
  'newproduct',
  'festival',
])

// 测试品牌 slug（保留全部体裁 + 话题输入框用于后续调试）
// 额外用「包含 haohong」做兜底，避免 slug 大小写/轻微差异导致误判为生产品牌。
const TEST_BRAND_SLUGS: ReadonlySet<string> = new Set<string>(['haohongtianxia'])

/** 是否为测试品牌：保留全部体裁与话题输入框 */
export function isTestBrand(brandKey: string | null | undefined): boolean {
  if (!brandKey) return false
  const k = brandKey.toLowerCase()
  return TEST_BRAND_SLUGS.has(k) || k.includes('haohong')
}

/** 按品牌返回「可选体裁」列表：生产品牌隐藏前 6 种，仅留 tier/checklist/chat */
export function getVisibleStyleOptions(brandKey?: string | null): StyleOption[] {
  if (isTestBrand(brandKey)) return STYLE_LIST
  return STYLE_LIST.filter((s) => !HIDDEN_LEGACY_STYLES.has(s.value))
}

/** 是否显示「输入话题关键词」输入框（仅测试品牌，因该输入存在已知 BUG） */
export function isTopicInputEnabled(brandKey?: string | null): boolean {
  return isTestBrand(brandKey)
}

/**
 * 历史笔记的旧风格值 → 新体裁中文标签（仅用于列表页展示，不参与任何请求）
 *
 * 老版本 H5 往库里写过 emotional/humorous 等语气值，
 * 没有这张表时 NoteCard 会把英文原值直接显示给老板。
 * 映射关系与后端 prompts.py 的 `_STYLE_ALIASES` 保持一致。
 */
export const LEGACY_STYLE_LABEL_MAP: Record<string, string> = {
  // 旧语气值（已废弃，仅用于历史笔记展示兜底）
  emotional: '真实测评',
  storytelling: '真实测评',
  casual: '话题互动',
  humorous: '话题互动',
  professional: '真实测评',
  interactive: '话题互动',
  controversial: '话题互动',
  trending: '节日应景',
  luxury: '必点榜单',
  // 已下线的历史落库体裁（2026-08 收敛到 6 种时移除），旧笔记展示兜底
  checkin: '探店打卡',
  daily: '随手日常',
  note: '门店介绍',
}

/**
 * 历史遗留的 style_type 落库值 → 中文标签（仅用于旧数据展示兜底）
 *
 * - `propagation`：品牌传播模块的**旧**落库值。该模块现已接住体裁参数，
 *   新笔记落库的是 9 项体裁之一；但库里的历史笔记仍是 propagation。
 * - `recruitment`：招募贴的落库值。**功能已下线**（其他文案模块整体移除），
 *   不再产生新数据，仅用于旧数据展示兜底。
 *
 * 这两个值用户在 StyleSelector 里都选不到（不属于 STYLE_LIST），
 * 但真实存在于库中；删掉映射会让老板在笔记列表里看到英文原值。
 */
export const MODULE_STYLE_LABEL_MAP: Record<string, string> = {
  // 历史遗留值，品牌传播已改存真实体裁，仅用于旧数据展示兜底
  propagation: '品牌传播',
  // 历史遗留值，功能已下线，仅用于旧数据展示兜底
  recruitment: '招募贴',
}

// 风格值到标签的快速映射（新体裁优先，旧值/模块专属值兜底显示中文）
export const STYLE_LABEL_MAP: Record<string, string> = STYLE_LIST.reduce(
  (map, item) => {
    map[item.value] = item.label
    return map
  },
  { ...LEGACY_STYLE_LABEL_MAP, ...MODULE_STYLE_LABEL_MAP } as Record<string, string>,
)

// ===== 3 大功能模块 =====
export interface ModuleOption {
  value: ModuleType
  label: string
  icon: string
  description: string
  color: string
}

export const MODULE_LIST: ModuleOption[] = [
  {
    value: 'traffic',
    label: '种草引流',
    icon: '🌱',
    description: '生成小红书种草笔记，吸引流量',
    color: '#FF8C00',
  },
  {
    value: 'comment',
    label: '评论互动',
    icon: '💬',
    description: '生成评论区互动话术，提升活跃',
    color: '#2196F3',
  },
  {
    value: 'brand',
    label: '品牌传播',
    icon: '🎨',
    description: '品牌传播图文，AI 生成配图',
    color: '#9C27B0',
  },
]

// 模块值到标签的快速映射
export const MODULE_LABEL_MAP: Record<ModuleType, string> = MODULE_LIST.reduce(
  (map, item) => {
    map[item.value] = item.label
    return map
  },
  {} as Record<ModuleType, string>,
)

/**
 * 后端尚未接线的模块：全端一致置灰并标「即将开放」（单一真源）
 *
 * 依据 docs/内容风格枚举方案.md 事实 B：`generate_comment()` 全后端无任何
 * 路由调用，只有 `/content/generate` 一个入口，且 `module_type` 是无枚举
 * 校验的裸 str。此时点进去只会拿到一篇普通种草笔记**并扣掉 1 次额度**，
 * 老板会误判成「AI 质量差」而不是「功能没做完」，真机测试结论会被污染。
 *
 * ⚠️ 任何入口（HomePage 卡片 / ModuleSelector / ContentPage 预选跳步）
 *    都必须引用本常量判断，不要各写各的副本。
 */
export const COMING_SOON_MODULES: ReadonlySet<ModuleType> = new Set<ModuleType>([
])

/** 模块是否「即将开放」（未接线，禁止进入生成流程） */
export function isComingSoonModule(module: ModuleType): boolean {
  return COMING_SOON_MODULES.has(module)
}

// ===== 按次计费（原月度/季度/年度方案已下线，2026-08-19）=====
export const PAY_PER_USE_INFO = {
  title: '按次计费',
  /**
   * 说明：按生成次数收费，单次金额由运营在品牌配置中设置（仅记录，不直接扣费）；
   * 可按批次购买额度（创建订单授予 quota_granted 条），该品牌下所有员工共用
   * 品牌总次数池（quota_total），用完即止。
   */
  description:
    '按生成次数收费，单次金额由运营在品牌配置中设置；可按批次购买额度，该品牌下所有员工共用总次数池，用完即止。',
}

// ===== 试用配置 =====
export const TRIAL_CONFIG = {
  duration_days: 7,
  daily_quota: 15,
}

// ===== localStorage 键名 =====
export const STORAGE_KEYS = {
  EMPLOYEE_ID: 'cy_employee_id',
  JWT: 'cy_jwt',
  BRAND_ID: 'cy_brand_id',
  NAME: 'cy_name',
  /** 客户侧角色：employee / boss（由登录密码判定，boss 才能进老板看板） */
  ROLE: 'cy_role',
  ADMIN_JWT: 'cy_admin_jwt',
  /** 壳内首次发布引导浮层是否已展示（PRD 4.5） */
  NATIVE_GUIDE_SHOWN: 'cy_native_guide_shown',
  /** 待回填上下文（结论 D：冷启动恢复用，原生壳 T01 登记） */
  PENDING_PUBLISH: 'chaoyinli_pending_publish',
  /**
   * 品牌主题色缓存（H5 动态换肤）：{ primary, secondary }
   * 持久化后冷启动即可用品牌配色渲染首屏，避免「先橙后变色」的闪烁
   */
  BRAND_THEME: 'cy_brand_theme',
} as const

// ===== API 配置 =====
export const API_CONFIG = {
  BASE_URL: '/api/v1',
  /**
   * 全局默认超时（毫秒）
   *
   * ⚠️ 这是「快接口」的超时（登录 / 额度 / 笔记列表等），故意保持短，
   *    后端一挂就快速失败，不让老板对着转圈等 4 分钟。
   *    AI 生成这类慢接口**不要**改这里，用下面的 AI_GENERATE_TIMEOUT 单独覆盖。
   */
  TIMEOUT: 30000,
} as const

/**
 * AI 内容生成接口专用超时（毫秒）
 *
 * 为什么单独一个常量而不是调大 API_CONFIG.TIMEOUT：
 * 真机实测 qwen3.7-plus 单次文案生成就要 30~34 秒，已经卡在 30 秒边缘；
 * 品牌传播模块还要叠加 qwen-image-3.0 出图（后端 WANX_TIMEOUT=120、
 * WANX_MAX_WAIT=180），整条链路远超 30 秒，axios 先于后端 abort，
 * 前端弹「请求超时，请稍后重试」而后端其实还在正常生成。
 *
 * 取值 240s：> 后端最坏链路（文案 180s + 出图 180s 不会同时打满，
 * 实测峰值约 150s），同时留出网络与序列化余量；
 * 必须 >= 后端 QWEN_TIMEOUT，否则前端先超时会让后端的重试/fallback 白做。
 *
 * 仅供 `/content/generate` 这一个接口通过 axios 单请求 config 覆盖使用。
 */
export const AI_GENERATE_TIMEOUT: number = 240000

// ===== 小红书相关 =====
export const XHS_CONFIG = {
  DOMAIN: 'xiaohongshu.com',
  /** iOS 唤起发布页（小红书官方 scheme 为 xhsdiscover，注意不是 xhsdiscovery） */
  IOS_SCHEME: 'xhsdiscover://post',
  /** iOS 唤起首页（发布页 scheme 失效时的备选） */
  IOS_SCHEME_HOME: 'xhsdiscover://home',
  /** Android 唤起发布页 */
  ANDROID_SCHEME: 'xhsdiscover://post',
  /** Android 唤起首页 */
  ANDROID_SCHEME_HOME: 'xhsdiscover://home',
  /** Android Chrome 系浏览器优先使用的 intent 协议（指定包名，成功率更高） */
  ANDROID_INTENT: 'intent://post#Intent;scheme=xhsdiscover;package=com.xingin.xhs;end',
  /** 唤起失败时的网页兜底地址 */
  WEB_URL: 'https://www.xiaohongshu.com/',
  DOWNLOAD_URL: 'https://www.xiaohongshu.com/download',
} as const

/**
 * 小红书「预填」唤起配置
 *
 * 与 `XHS_CONFIG` 里的裸 scheme 的区别：
 * 裸 scheme（`xhsdiscover://post`）只能把用户送到**空白**发布页，文案要靠手动粘贴；
 * 预填 scheme（`xhsdiscover://post_new_note?attach=<JSON>`）能让小红书 APP
 * 直接把标题/正文/图片填进发布器 —— 这条路**不需要**开放平台 AppKey，
 * 也不需要任何原生代码，是本期「一键发布」的主链路。
 *
 * `{attach}` 为运行时占位符，由 `buildPrefillCandidates()` 替换为
 * `encodeURIComponent(JSON.stringify(payload))`。
 * 注意：encodeURIComponent 会把 `#` 编码成 `%23`，因此 attach 内容
 * 不会截断 Android intent:// 语法里的 `#Intent;...;end` 片段。
 */
export const XHS_PREFILL_CONFIG = {
  /** attach 载荷里的字段名（小红书 APP 侧约定，勿改） */
  ATTACH_KEYS: {
    TITLE: 'note_title',
    TEXT: 'note_text',
    IMAGES: 'image_resources',
  },
  /**
   * iOS 候选（按成功率从高到低）
   *
   * iOS 不支持 intent://，只能用纯 scheme；末位保留「无 attach 的发布页」，
   * 保证即使预填参数被新版 APP 拒绝，用户至少还是落在发布页而非首页。
   */
  IOS_CANDIDATES: [
    'xhsdiscover://post_new_note?attach={attach}',
    'xhsdiscover://post_new_note',
  ],
  /**
   * Android 候选（按成功率从高到低）
   *
   * Chrome 系浏览器对 intent:// 的放行率高于裸 scheme，故置于次位做补刀；
   * 末位同样保留无 attach 的发布页兜底。
   */
  ANDROID_CANDIDATES: [
    'xhsdiscover://post_new_note?attach={attach}',
    'intent://post_new_note?attach={attach}#Intent;scheme=xhsdiscover;package=com.xingin.xhs;end',
    'xhsdiscover://post_new_note',
  ],
  /** 占位符字面量 */
  ATTACH_PLACEHOLDER: '{attach}',
} as const

/**
 * 原生壳（Capacitor）相关配置
 *
 * 供 H5 侧 Bridge 门面层与发布状态机共用；原生侧的 AppKey 等敏感配置
 * **绝不**出现在这里（见架构 §6.6）。
 */
export const NATIVE_CONFIG = {
  /** Capacitor 插件名，三端必须一致 */
  PLUGIN_NAME: 'XhsShare',
  /** 分享结果事件名，三端必须一致 */
  SHARE_RESULT_EVENT: 'xhsShareResult',
  /**
   * H5 侧契约版本号
   *
   * 与壳返回的 `shellInfo.bridgeVersion` 比对做能力协商：
   * 壳版本低于所需能力版本时，H5 主动降级而不是抛错。
   */
  BRIDGE_VERSION: 1,
  /** 全局挂载到 window 的门面名（仅供调试，业务代码不得依赖） */
  GLOBAL_NAMESPACE: 'ChaoyinliNative',
  /** URL 参数：浏览器里模拟壳内 UI，如 `?mocknative=1` */
  MOCK_QUERY_KEY: 'mocknative',

  // ===== 超时阈值（毫秒）=====
  /** 调起后等待 `xhsShareResult` 事件的上限，超时按「已回到本页」处理 */
  SHARE_RESULT_TIMEOUT: 120000,
  /** resume 后等待结果事件的兜底窗口（架构 §4.1：2s 内没等到事件就自行判定） */
  RESUME_FALLBACK_DELAY: 2000,
  /** 单次跨桥调用的超时保护 */
  BRIDGE_CALL_TIMEOUT: 8000,

  // ===== 图片规格（架构 §6.3）=====
  IMAGE: {
    /** 单张跨桥 base64 上限 2MB */
    MAX_BASE64_BYTES: 2 * 1024 * 1024,
    /** JPEG 质量递降梯度：超限时依次降质重试 */
    QUALITY_STEPS: [0.9, 0.7, 0.5],
    /** 栅格化目标短边（小红书建议短边 ≥ 720px） */
    MIN_SHORT_EDGE: 720,
    /** 小红书单次最多 9 张图 */
    MAX_COUNT: 9,
    /** 栅格化输出格式 */
    OUTPUT_MIME: 'image/jpeg',
  },

  /** 待回填记录有效期：30 分钟（结论 D / P0-12） */
  PENDING_PUBLISH_TTL: 30 * 60 * 1000,
} as const
